import { authHeaders, requestJson } from './http';
import type { Voice } from '../context/AppContext';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

export async function fetchVoicesApi(signal?: AbortSignal): Promise<Voice[]> {
  return requestJson<Voice[]>(`${API_BASE_URL}/voice/voices`, {
    headers: authHeaders(true),
    signal,
  });
}

export interface VoiceStatusResponse {
  voice_id: string;
  status: Voice['status'];
}

export async function uploadVoiceSamplesApi(
  voiceId: string,
  files: File[],
  nickname?: string,
): Promise<string> {
  const formData = new FormData();
  formData.append('voice_id', voiceId);
  if (nickname) formData.append('voice_name', nickname);
  files.forEach((file) => formData.append('files', file));
  const endpoint = voiceId.startsWith('voice_')
    ? `${API_BASE_URL}/voice/upload-samples/${encodeURIComponent(voiceId)}`
    : `${API_BASE_URL}/voice/onboard`;
  const result = await requestJson<VoiceStatusResponse>(endpoint, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
  if (!result.voice_id) throw new Error('The voice upload did not return a voice ID.');
  return result.voice_id;
}

export async function trainVoiceApi(voiceId: string): Promise<VoiceStatusResponse> {
  return requestJson<VoiceStatusResponse>(`${API_BASE_URL}/voice/train-voice`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ voice_id: voiceId }),
  });
}

export async function fetchVoiceStatusApi(voiceId: string): Promise<VoiceStatusResponse> {
  return requestJson<VoiceStatusResponse>(`${API_BASE_URL}/voice/voice-status/${encodeURIComponent(voiceId)}`, {
    headers: authHeaders(),
  });
}

export async function deleteVoiceApi(voiceId: string): Promise<{ message: string }> {
  return requestJson<{ message: string }>(`${API_BASE_URL}/voice/voices/${encodeURIComponent(voiceId)}`, {
    method: 'DELETE',
    headers: authHeaders(true),
  });
}

export async function retrainVoiceApi(voiceId: string): Promise<{ voice_id: string }> {
  return requestJson<{ voice_id: string }>(`${API_BASE_URL}/voice/voices/${encodeURIComponent(voiceId)}/retrain`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ voice_id: voiceId }),
  });
}

export async function updateVoiceNicknameApi(voiceId: string, voiceName: string): Promise<Voice> {
  return requestJson<Voice>(`${API_BASE_URL}/voice/voices/${encodeURIComponent(voiceId)}/nickname`, {
    method: 'PATCH',
    headers: authHeaders(true),
    body: JSON.stringify({ voice_name: voiceName }),
  });
}
